/**
 * Mobile chat bottom sheet component
 * Uses Headless UI Dialog for accessible mobile chat experience
 */

import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Fragment, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useChatWebSocket } from "@/hooks/useChatWebSocket";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function ChatMobileSheet({ isOpen, onClose }: Props) {
  const { apiKey } = useAuth();
  const queryClient = useQueryClient();

  // Refetch queries when AI agent completes (may have created/modified data)
  const handleChatComplete = useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ["invoices"], type: "all" }),
      queryClient.refetchQueries({ queryKey: ["registries"], type: "all" }),
      queryClient.refetchQueries({ queryKey: ["invoiceCount"], type: "all" }),
    ]);
  }, [queryClient]);

  const { messages, sendMessage, isConnected, isStreaming } = useChatWebSocket({
    apiKey: apiKey || "",
    onComplete: handleChatComplete,
  });

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50 lg:hidden">
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </TransitionChild>

        {/* Sheet */}
        <TransitionChild
          as={Fragment}
          enter="transition-transform duration-200"
          enterFrom="translate-y-full"
          enterTo="translate-y-0"
          leave="transition-transform duration-150"
          leaveFrom="translate-y-0"
          leaveTo="translate-y-full"
        >
          <DialogPanel className="fixed inset-x-0 bottom-0 flex h-[85vh] flex-col rounded-t-2xl bg-white dark:bg-gray-900">
            {/* Drag handle */}
            <div className="flex justify-center py-2">
              <div className="h-1 w-12 rounded-full bg-gray-300 dark:bg-gray-700" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 pb-3 dark:border-gray-700">
              <DialogTitle className="font-semibold text-gray-900 dark:text-gray-100">AI Assistant</DialogTitle>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid="chat-mobile-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center p-8 text-center text-gray-500">
                  <p className="mb-4">How can I help you today?</p>
                  <div className="space-y-2 text-sm">
                    <button
                      type="button"
                      onClick={() => sendMessage("Show unpaid invoices")}
                      className="block w-full rounded-lg border p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                    >
                      Show unpaid invoices
                    </button>
                  </div>
                </div>
              ) : (
                messages.map((msg) => (
                  <ChatMessage key={msg.id} role={msg.role} content={msg.content} isStreaming={msg.isStreaming} />
                ))
              )}
            </div>

            {/* Input */}
            <ChatInput onSend={sendMessage} disabled={!isConnected || isStreaming} />
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}
